/* @flow */
import * as React from "react";
import styled, { css } from "styled-components";

import ClickOutside from "../common/ClickOutside";

const SelectContainer = styled.div`
  display: inline-block;
  vertical-align: bottom;
  width: 104px;
  padding: 0 6px;
  box-sizing: border-box;
  text-align: left;

  @media(max-width: 639px) {
    width: 79px;
  }

  @media(max-width: 567px) {
    width: 69px;
  }
`;

const SelectView = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
  height: 29px;
  border: 1px solid ${props => props.theme.borderMainColor};
  border-radius: 3px;
  font-weight: 300;
  font-size: ${({theme}) => theme.locale === "ar" ? theme.minFontSize : "14px"};
  font-family: Roboto, Arial, sans-serif;
  line-height: 27px;
  color: ${({ theme }) => theme.foreground};
  padding: 0px;
  background-color: ${props => props.theme.mainHighlight};
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAkAAAAFCAYAAACXU8ZrAAABS2lUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSLvu78iIGlkPSJXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQiPz4KPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iQWRvYmUgWE1QIENvcmUgNS42LWMxMzggNzkuMTU5ODI0LCAyMDE2LzA5LzE0LTAxOjA5OjAxICAgICAgICAiPgogPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIi8+CiA8L3JkZjpSREY+CjwveDp4bXBtZXRhPgo8P3hwYWNrZXQgZW5kPSJyIj8+IEmuOgAAACpJREFUCJljnDl17n8GAoCJgYGBkYAaRiYYA5cCmEkMOBTC+Uw4JFA0AAADHALciUEQ5gAAAABJRU5ErkJggg==');
  background-position: calc(100% - 7px) 11px;
  background-size: 9px 5px;
  background-repeat: no-repeat;
  box-shadow: inset 0px 2px 0px 0px rgba(0,0,0,.15);
`;

const Option = styled.li`
  cursor: pointer;
  padding: 0 7px;

  :hover {
    background-color: ${props => props.theme.mainDark};
  }
`;

const OptionContainer = styled.ul`
  display: block;
  position: absolute;
  top: 25px;
  left: 50%;
  box-sizing: border-box;
  padding: 2px 0 0;
  transform: translateX(-50%);
  width: calc(100% + 2px);
  max-height: 200px;
  overflow: auto;
  background-color: ${props => props.theme.mainHighlight};
  border: 1px solid ${props => props.theme.borderMainColor};
  border-top: none;
  border-radius: 0 0 3px 3px;
  z-index: 250;
`;

const ColorSwatch = styled.span`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  top: -1px;
  width: 15px;
  height: 15px;
`;

const Selected = styled.p`
  margin: 0;
  padding: 0 7px;
`;

const Label = styled.div`
  direction: ${props => props.theme.direction};
  margin: 5px 0;
  padding: 0;
  font-size: ${({theme}) => theme.locale === "ar" ? theme.minFontSize : "12px"};
  line-height: 1;
  color: #99959d;

  ${props => props.theme.direction === "rtl" && css`
    text-align: right;
  `};
`;

function Color({ children }: { children: string }) {
  return <ColorSwatch style={{ backgroundColor: children }} />;
}

function Pixels({ children }: { children: number }) {
  return <span>{children} px</span>;
}

type Props<TValue> = {
  type?: "color" | "pixels",
  name: string,
  label: React.Node,
  value: TValue,
  values: Array<TValue>,
  onChange: (nextValue: TValue) => void
};

class Select extends React.Component<Props<TValue>> {
  state = {
    isOpen: false
  };

  open = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isOpen: true });
  };

  close = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isOpen: false });
  };

  render() {
    const { type, name, label, value, values, onChange, width } = this.props;

    let Value = "span";

    switch (type) {
      case "color":
        Value = Color;
        break;
      case "pixels":
        Value = Pixels;
        break;
      default:
        break;
    }


    return (
      <SelectContainer width={width}>
        {label && <Label>
          {label}:
        </Label>}
        <SelectView>
          <Selected onClick={this.open}>
            <Value>{value}</Value>
          </Selected>
          {this.state.isOpen &&
            <ClickOutside onOutsideClick={this.close}>
              <OptionContainer type={type}>
                {values.map(value =>
                  <Option
                    type={type}
                    key={value}
                    onClick={() => this.setState({ isOpen: false },
                      () => onChange({ target: { name, value } })
                    )}
                  >
                    {<Value active={value === this.props.value}>{value}</Value>}
                  </Option>
                )}
              </OptionContainer>
            </ClickOutside>}
        </SelectView>
      </SelectContainer>
    );
  }
}

export default Select;



// WEBPACK FOOTER //
// ./src/components/Popup/Select.js